import { forwardRef, useId, type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';
import { FieldLabel } from '@/shared/ui/form-field/FieldLabel';
import { InlineFieldLabel } from '@/shared/ui/form-field/InlineFieldLabel';
import { FieldMessage } from '@/shared/ui/form-field/FieldMessage';
import { Input } from '@/shared/ui/input';

export interface TextBoxProps extends Omit<ComponentProps<'input'>, 'id' | 'size'> {
  label: string;
  helperText?: string;
  error?: string;
  id?: string;
  /** Rozložení labelu: `'stacked'` (default, label nahoře) nebo `'inline'` (label vlevo, tabulkový vzhled). */
  layout?: 'stacked' | 'inline';
  /** Šířka levého labelu v px (jen `layout="inline"`). Default 104. */
  labelWidth?: number;
}

/**
 * Labeled text input, Phoenix-styled. Stacked layout (label nahoře) je default; `layout="inline"`
 * dá label vlevo (tabulkový vzhled, šířka `labelWidth`).
 */
export const TextBox = forwardRef<HTMLInputElement, TextBoxProps>(function TextBox(
  {
    label,
    helperText,
    error,
    disabled,
    readOnly,
    required,
    className,
    id: externalId,
    layout = 'stacked',
    labelWidth = 104,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = externalId ?? generatedId;
  const messageId = `${inputId}-message`;
  const hasError = Boolean(error);
  const isInline = layout === 'inline';

  const input = (
    <Input
      ref={ref}
      id={inputId}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      invalid={hasError}
      aria-invalid={hasError || undefined}
      aria-describedby={helperText || error ? messageId : undefined}
      className={cn(readOnly && 'bg-phx-surface-2 cursor-default')}
      {...props}
    />
  );

  const message =
    error || helperText ? (
      <FieldMessage id={messageId} variant={hasError ? 'error' : 'helper'}>
        {error ?? helperText}
      </FieldMessage>
    ) : null;

  if (isInline) {
    return (
      <div
        data-layout="inline"
        className={cn('phx-field gap-phx-4 flex min-w-0 items-start', className)}
      >
        <InlineFieldLabel
          htmlFor={inputId}
          required={required}
          disabled={disabled}
          width={labelWidth}
        >
          {label}
        </InlineFieldLabel>
        <div className="gap-phx-2 flex min-w-0 flex-1 flex-col">
          {input}
          {message}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('phx-field gap-phx-2 flex min-w-0 flex-col', className)}>
      <FieldLabel htmlFor={inputId} required={required} disabled={disabled}>
        {label}
      </FieldLabel>
      {input}
      {message}
    </div>
  );
});
